import { FC, useContext, useEffect, useState } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { IconWorld, IconWorldOff } from '@tabler/icons-react'

import { validatePlanForTools } from '@/lib/subscription'
import { cn } from '@/lib/utils'
import { safeConsole } from '@/lib/utils/safeConsole'

import { Button } from '../ui/button'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

export const WebBrowseToggle: FC = () => {
  const { profile, tools, setIsPaywallOpen, allModels } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )
  const { chatSettings, selectedTools, setSelectedTools } = useContext(ChatbotUIChatContext)
  const [toolsChecked, setToolsChecked] = useState(false)
  
  // Try multiple possible names for the web browsing tool
  const webBrowsingTool = tools?.find(
    (tool) => 
      tool.name === 'Web Browsing' || 
      tool.name === 'Web Browsing Tool' ||
      tool.name === 'web_browsing' ||
      (tool.name?.toLowerCase().includes('web') && tool.name?.toLowerCase().includes('brows'))
  )
  
  const isWebBrowsingEnabled = selectedTools.some((t) => t.id === webBrowsingTool?.id)
  
  // Always log for debugging - helps verify component is rendering
  const isPublicUser = !profile
  if (isPublicUser) {
    safeConsole.log('WebBrowseToggle rendered - Public user', {
      hasProfile: !!profile,
      toolsLength: tools?.length || 0,
      chatSettingsModel: chatSettings?.model,
      chatSettingsExists: !!chatSettings,
      selectedToolsCount: selectedTools.length,
      webBrowsingToolFound: !!webBrowsingTool
    })
  }

  // Check if current model supports tools
  const selectedModel = allModels.find(
    (x) => x.modelId === chatSettings?.model || x.hostedId === chatSettings?.model
  )

  // For public users (no profile), always show if we have the tool and losi-mini model
  const isLosiMini = chatSettings?.model === 'losi-mini' || (!chatSettings && isPublicUser) // Default to losi-mini for public users
  
  // For public users, wait for tools to load
  useEffect(() => {
    if (isPublicUser) {
      // Mark as checked once tools are available or after a delay
      if (tools && tools.length > 0) {
        setToolsChecked(true)
      } else {
        // Give tools more time to load (platform tools are loaded asynchronously)
        const timer = setTimeout(() => {
          setToolsChecked(true)
        }, 3000)
        return () => clearTimeout(timer)
      }
    } else {
      setToolsChecked(true)
    }
  }, [isPublicUser, tools])
  
  // Debug logging for public users
  useEffect(() => {
    if (isPublicUser) {
      safeConsole.log('WebBrowseToggle - Public user debug:', {
        toolsLength: tools?.length || 0,
        toolsChecked,
        webBrowsingTool: !!webBrowsingTool,
        webBrowsingToolName: webBrowsingTool?.name,
        allToolNames: tools?.map(t => t.name) || [],
        isLosiMini,
        chatSettingsModel: chatSettings?.model,
        selectedToolsCount: selectedTools.length,
        profile: !!profile
      })
    }
  }, [isPublicUser, tools, toolsChecked, webBrowsingTool, chatSettings, selectedTools, profile, isLosiMini])
  
  // For public users, ALWAYS show the toggle (even if tool not loaded yet)
  // This ensures consistent UI for public chat
  if (isPublicUser) {
    // Always show - either placeholder or actual toggle
    if (!webBrowsingTool) {
      // Tool not loaded yet - show disabled placeholder
      return (
        <Button
          disabled
          className="hover:bg-accent cursor-not-allowed rounded-lg p-2 opacity-50 transition-colors"
          variant="ghost"
          size="sm"
          title="Loading web browsing tool..."
        >
          <IconWorldOff
            size={20}
            className="text-muted-foreground transition-all duration-200 dark:text-white"
            stroke={2}
          />
        </Button>
      )
    }
    // Tool found - continue to render toggle below
  } else if (!selectedModel) {
    // For authenticated users, need model to be found
    return null
  } else if (selectedModel && !selectedModel.tools) {
    // Model doesn't support tools
    return null
  } else if (!webBrowsingTool) {
    // For authenticated users, tool must exist
    return null
  }
  
  // If we get here and webBrowsingTool is null, something went wrong
  if (!webBrowsingTool) {
    safeConsole.warn('WebBrowseToggle - webBrowsingTool is null but should have been handled above')
    return null
  }

  const handleToggle = () => {
    if (!webBrowsingTool || !chatSettings) return

    // Skip validation for public users (no profile)
    if (profile && !validatePlanForTools(profile, [webBrowsingTool], chatSettings.model)) {
      scheduleUpgradePaywall()
      return
    }

    if (isWebBrowsingEnabled) {
      setSelectedTools(selectedTools.filter((t) => t.id !== webBrowsingTool.id))
    } else {
      setSelectedTools([...selectedTools, webBrowsingTool])
    }
  }

  return (
    <Button
      onClick={handleToggle}
      className={cn(
        'hover:bg-accent rounded-lg p-2 transition-colors',
        isWebBrowsingEnabled && 'bg-blue-50 dark:bg-blue-900/20'
      )}
      variant="ghost"
      size="sm"
    >
      {isWebBrowsingEnabled ? (
        <IconWorld
          size={20}
          className="font-bold text-black transition-all duration-200 dark:text-white"
          stroke={2.3}
        />
      ) : (
        <IconWorldOff
          size={20}
          className="text-muted-foreground transition-all duration-200 dark:text-white"
          stroke={2}
        />
      )}
    </Button>
  )
}
